import { withStyles } from '@material-ui/core/styles';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import DirectionsCarIcon from '@material-ui/icons/DirectionsCar';
import EventAvailableIcon from '@material-ui/icons/EventAvailable';
import FlagIcon from '@material-ui/icons/Flag';
import HotelIcon from '@material-ui/icons/Hotel';
import SecurityIcon from '@material-ui/icons/Security';
import { marked } from 'marked';
import PropTypes from 'prop-types';
import React from 'react';
import { uuid } from 'utils/common';
import ActivityCard from './activityCard';
import transformDayWiseItinerary from './itineraryDataTransformers';
import StayCard from './stayCard';

// Map icon names to Material-UI icon components
const getIconComponent = (iconName) => {
  const iconMap = {
    bed: HotelIcon,
    flag: FlagIcon,
    car: DirectionsCarIcon,
    shield: SecurityIcon,
    attach: AttachFileIcon,
    calendar: EventAvailableIcon,
  };
  return iconMap[iconName?.toLowerCase()] || HotelIcon;
};

// Color palette for highlights with background and icon colors
const highlightColors = [
  { bg: '#E3F2FD', icon: '#2196F3' }, // Light blue background, blue icon
  { bg: '#E8F5E9', icon: '#4CAF50' }, // Light green background, green icon
  { bg: '#F3E5F5', icon: '#9C27B0' }, // Light purple background, purple icon
  { bg: '#FFE0E0', icon: '#F44336' }, // Light peach background, red icon
  { bg: '#FFF3E0', icon: '#FF9800' }, // Light orange background, orange icon
  { bg: '#E1F5FE', icon: '#03A9F4' }, // Light cyan background, cyan icon
];

const ItinerarySection = ({
  onViewGallery, itineraryData, classes,
}) => {
  const dayWiseData = itineraryData ? transformDayWiseItinerary(itineraryData) : [];
  const inclusions = itineraryData?.inclusions;
  const exclusions = itineraryData?.exclusions;
  const cancellationPolicies = itineraryData?.cancellationPolicies;

  if (!itineraryData) {
    return null;
  }

  const stays = (itineraryData.itineraryParts || []).filter((part) => part.type === 'STAY' && !part.stayData?.isCruise);
  const cruises = (itineraryData.itineraryParts || []).filter((part) => part.type === 'STAY' && part.stayData?.isCruise);
  const highlights = itineraryData?.highlights || [];
  const isMaldives = itineraryData?.requirement?.places?.[0]?.countryName?.toLowerCase() === 'maldives';

  return (
    <section className={classes.section}>
      {highlights.length > 0 && (
        <>
          <br />
          <br />
          <h2 className={classes.sectionTitle}>Highlights</h2>
          <div className={classes.highlightsContainer}>
            {highlights.map((highlight, index) => {
              const IconComponent = getIconComponent(highlight.icon);
              const colorScheme = highlightColors[index % highlightColors.length];
              return (
                <div
                  key={uuid()}
                  className={classes.highlightCard}
                  style={{ backgroundColor: colorScheme.bg }}
                >
                  <IconComponent className={classes.highlightIcon} style={{ color: colorScheme.icon }} />
                  <span className={classes.highlightText}>{highlight.text}</span>
                </div>
              );
            })}
          </div>
        </>
      )}
      <h2 className={classes.sectionTitle}>Stays</h2>
      <div className={classes.staysContainer}>
        {
          stays.map((stay) => {
            return <StayCard key={uuid()} itineraryPart={stay} onViewGallery={onViewGallery} />
          })
        }
      </div>

      {
        cruises.length > 0 && (
          <>
            <h2 className={classes.cruiseTitle}>Cruise</h2>
            {
              cruises.map((cruise) => {
                return <StayCard key={uuid()} itineraryPart={cruise} onViewGallery={onViewGallery} />
              })
            }
          </>
        )
      }

      {isMaldives && (inclusions?.length > 0 || exclusions?.length > 0) && (
        <>
          {inclusions && inclusions.length > 0 && (
            <>
              <h2 className={classes.inclusionsTitle}>Inclusions</h2>
              <section className={classes.inclusionsExclusionsSection}>
                <ul className={classes.inclusionsExclusionsList}>
                  {inclusions.map((item) => (
                    <li key={item} className={classes.inclusionsExclusionsItem}>{item}</li>
                  ))}
                </ul>
              </section>
            </>
          )}
          {exclusions && exclusions.length > 0 && (
            <>
              <h2 className={classes.exclusionsTitle}>Exclusions</h2>
              <section className={classes.inclusionsExclusionsSection}>
                <ul className={classes.inclusionsExclusionsList}>
                  {exclusions.map((item) => (
                    <li key={item} className={classes.inclusionsExclusionsItem}>{item}</li>
                  ))}
                </ul>
              </section>
            </>
          )}
        </>
      )}

      <h2 className={classes.itineraryTitle}>
        Itinerary
      </h2>
      {dayWiseData.map((day) => (
        <div key={day.day} className={classes.dayContainer}>
          <div className={classes.dayHeader}>
            <h3 className={classes.dayTitle}>
              Day {day.day}
            </h3>
            <span className={classes.dayDate}>
              {day.date}
            </span>
          </div>
          {/* Stack description, stay, and activity with 32px gap */}
          <div className={classes.dayContent}>
            {/* eslint-disable-next-line react/no-danger */}
            <div
              className={classes.description}
              dangerouslySetInnerHTML={{ __html: marked.parse(day.description || '') }}
            />
            {day.stays && day.stays.map((stay) => (
              <StayCard key={uuid()} itineraryPart={stay} onViewGallery={onViewGallery} />
            ))}
            {day.activities && day.activities.map((activity, index) => (
              <ActivityCard
                key={`activity-${day.day}-${activity.activityData?.experienceId || index}`}
                itineraryPart={activity}
                onViewGallery={onViewGallery}
              />
            ))}
          </div>
        </div>
      ))}
      {/* Render Stays, Inclusions, Exclusions as sections below itinerary */}
      <div className={classes.bottomSection}>
        {!isMaldives && inclusions && inclusions.length > 0 && (
          <>
            <h2 className={classes.inclusionsTitle}>Inclusions</h2>
            <section className={classes.inclusionsExclusionsSection}>
              <ul className={classes.inclusionsExclusionsList}>
                {inclusions.map((item) => (
                  <li key={item} className={classes.inclusionsExclusionsItem}>{item}</li>
                ))}
              </ul>
            </section>
          </>
        )}
        {!isMaldives && exclusions && exclusions.length > 0 && (
          <>
            <h2 className={classes.exclusionsTitle}>Exclusions</h2>
            <section className={classes.inclusionsExclusionsSection}>
              <ul className={classes.inclusionsExclusionsList}>
                {exclusions.map((item) => (
                  <li key={item} className={classes.inclusionsExclusionsItem}>{item}</li>
                ))}
              </ul>
            </section>
          </>
        )}
        {cancellationPolicies && cancellationPolicies.length > 0 && (
          <>
            <h2 className={classes.cancellationPoliciesTitle}>Cancellation Policies</h2>
            <section className={classes.cancellationPoliciesSection}>
              <ul className={classes.cancellationPoliciesList}>
                {cancellationPolicies.map((item) => (
                  <li key={item.text} className={classes.cancellationPoliciesItem}>
                    {item.link ? (
                      <a href={item.link} target="_blank" rel="noopener noreferrer" className={classes.cancellationPolicyLink}>
                        {item.text}
                      </a>
                    ) : (
                      item.text
                    )}
                  </li>
                ))}
              </ul>
            </section>
          </>
        )}
      </div>
    </section>
  );
};

const styles = (theme) => ({
  section: {
    marginTop: 48,
    width: '100%',
    color: '#32302C',
  },
  highlightsContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: '16px',
    marginBottom: 32,
    width: '100%',
  },
  highlightCard: {
    display: 'flex',
    alignItems: 'center',
    padding: '16px 24px',
    borderRadius: 12,
    height: 42,
    gap: '16px',
  },
  highlightIcon: {
    fontSize: 24,
    color: '#32302C',
    flexShrink: 0,
  },
  highlightText: {
    fontSize: 16,
    fontWeight: 500,
    color: '#32302C',
    lineHeight: 1.5,
  },
  sectionTitle: {
    fontSize: 30,
    fontWeight: 700,
    margin: '30px 0 32px 0',
  },
  staysContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '30px',
  },
  cruiseTitle: {
    fontSize: 30,
    fontWeight: 700,
    margin: '30px 0 32px 0',
  },
  itineraryTitle: {
    fontSize: 30,
    fontWeight: 700,
    margin: 0,
    marginBottom: 8,
    marginTop: 30,
  },
  dayContainer: {
    marginBottom: 50,
  },
  dayHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 10,
  },
  dayTitle: {
    fontSize: 24,
    fontWeight: 700,
    margin: 0,
    marginRight: 8,
  },
  dayDate: {
    fontSize: 16,
    fontWeight: 400,
    color: '#73726E',
  },
  dayContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '30px',
  },
  description: {
    fontSize: 16,
    lineHeight: '32px',
    color: '#32302C',
  },
  bottomSection: {
    marginTop: 64,
  },
  inclusionsTitle: {
    fontSize: 30,
    fontWeight: 700,
    margin: '30px 0 0 0',
  },
  exclusionsTitle: {
    fontSize: 30,
    fontWeight: 700,
    marginTop: 0,
  },
  cancellationPoliciesTitle: {
    fontSize: 30,
    fontWeight: 700,
  },
  inclusionsExclusionsSection: {
    margin: '0 auto',
    width: '100%',
    fontFamily: 'Sofia-pro, sans-serif',
    color: '#32302C',
    padding: 20,
  },
  inclusionsExclusionsList: {
    fontSize: 16,
    lineHeight: '32px',
    paddingLeft: 24,
    margin: 0,
  },
  inclusionsExclusionsItem: {
    marginBottom: 8,
  },
  cancellationPoliciesSection: {
    margin: '0 auto',
    width: '100%',
    fontFamily: 'Sofia-pro, sans-serif',
    color: '#32302C',
    padding: 20,
  },
  cancellationPoliciesList: {
    fontSize: 16,
    lineHeight: '32px',
    paddingLeft: 24,
    margin: 0,
  },
  cancellationPoliciesItem: {
    marginBottom: 8,
  },
  cancellationPolicyLink: {
    color: theme.colors.textDark,
    textDecoration: 'underline',
  },
});

ItinerarySection.propTypes = {
  onViewGallery: PropTypes.func,
  itineraryData: PropTypes.object,
  isLoading: PropTypes.bool,
  classes: PropTypes.object,
};

export default withStyles(styles)(ItinerarySection); 
